import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PracticeList } from '@/types/settings-api';

/**
 * Practice list for the Practices tab (PROD-757).
 *   archived=false → GET /api/v1/Practice/List/{orgId}
 *   archived=true  → GET /api/Practice/ArchivePracticeList/{orgId}
 *
 * Flutter splits the active/archived lists across separate endpoints AND
 * across two different API path prefixes — Active lives under `/api/v1/Practice`
 * (Flutter `settings_data_source_impl.dart:808`, `PracticeService.Practice_v1`)
 * while Archive lives under `/api/Practice` (line 462, `PracticeService.Practice`).
 * Don't normalise the prefix — they really are different controllers on the
 * .NET side.
 *
 * Same query key by (orgId, archived) so the tab toggle re-fetches cleanly.
 */
export function usePracticesList(
  organizationId: string | null | undefined,
  archived: boolean,
) {
  return useQuery({
    queryKey: ['settings', 'practices-list', organizationId, archived],
    queryFn: ({ signal }) => {
      if (!organizationId) throw new Error('usePracticesList called without orgId');
      const path = archived
        ? `/api/Practice/ArchivePracticeList/${organizationId}`
        : `/api/v1/Practice/List/${organizationId}`;
      return get<PracticeList[]>(path, { signal });
    },
    enabled: !!organizationId,
  });
}
